import { isString } from 'vega-util';
import { isBinning } from '../bin';
import { channelDefType, isFieldDef, isFieldOrDatumDefForTimeFormat, isScaleFieldDef, vgField } from '../channeldef';
import { fieldValidPredicate } from '../predicate';
import { ScaleType } from '../scale';
import { formatExpression, normalizeTimeUnit, timeUnitSpecifierExpression } from '../timeunit';
import { QUANTITATIVE } from '../type';
import { stringify } from '../util';
import { isSignalRef } from '../vega.schema';
import { datumDefToExpr } from './mark/encode/valueref';
export function isCustomFormatType(formatType) {
  return formatType && formatType !== 'number' && formatType !== 'time';
}

function customFormatExpr(formatType, field, format) {
  return "".concat(formatType, "(").concat(field).concat(format ? ", ".concat(stringify(format)) : '', ")");
}

export var BIN_RANGE_DELIMITER = " \u2013 ";
export function formatSignalRef(_ref) {
  var fieldOrDatumDef = _ref.fieldOrDatumDef,
      format = _ref.format,
      formatType = _ref.formatType,
      expr = _ref.expr,
      normalizeStack = _ref.normalizeStack,
      config = _ref.config;

  var _a, _b;

  if (isCustomFormatType(formatType)) {
    return formatCustomType({
      fieldOrDatumDef: fieldOrDatumDef,
      format: format,
      formatType: formatType,
      expr: expr,
      config: config
    });
  }

  var field = fieldToFormat(fieldOrDatumDef, expr, normalizeStack);
  var type = channelDefType(fieldOrDatumDef);

  if (type === 'quantitative' && format === undefined && formatType === undefined && config.customFormatTypes && config.numberFormatType) {
    return formatCustomType({
      fieldOrDatumDef: fieldOrDatumDef,
      format: config.numberFormat,
      formatType: config.numberFormatType,
      expr: expr,
      config: config
    });
  }

  if (isFieldOrDatumDefForTimeFormat(fieldOrDatumDef)) {
    var signal = timeFormatExpression(field, isFieldDef(fieldOrDatumDef) ? (_a = normalizeTimeUnit(fieldOrDatumDef.timeUnit)) === null || _a === void 0 ? void 0 : _a.unit : undefined, format, config.timeFormat, isScaleFieldDef(fieldOrDatumDef) && ((_b = fieldOrDatumDef.scale) === null || _b === void 0 ? void 0 : _b.type) === ScaleType.UTC);
    return signal ? {
      signal: signal
    } : undefined;
  }

  format = numberFormat(type, format, config);

  if (isFieldDef(fieldOrDatumDef) && isBinning(fieldOrDatumDef.bin)) {
    var endField = vgField(fieldOrDatumDef, {
      expr: expr,
      binSuffix: 'end'
    });
    return {
      signal: binFormatExpression(field, endField, format, formatType, config)
    };
  } else if (format || channelDefType(fieldOrDatumDef) === 'quantitative') {
    return {
      signal: "".concat(formatExpr(field, format))
    };
  } else {
    return {
      signal: "isValid(".concat(field, ") ? ").concat(field, " : \"\"+").concat(field)
    };
  }
}

function fieldToFormat(fieldOrDatumDef, expr, normalizeStack) {
  if (isFieldDef(fieldOrDatumDef)) {
    if (normalizeStack) {
      return "".concat(vgField(fieldOrDatumDef, {
        expr: expr,
        suffix: 'end'
      }), "-").concat(vgField(fieldOrDatumDef, {
        expr: expr,
        suffix: 'start'
      }));
    } else {
      return vgField(fieldOrDatumDef, {
        expr: expr
      });
    }
  } else {
    return datumDefToExpr(fieldOrDatumDef);
  }
}

export function formatCustomType(_ref2) {
  var fieldOrDatumDef = _ref2.fieldOrDatumDef,
      format = _ref2.format,
      formatType = _ref2.formatType,
      expr = _ref2.expr,
      normalizeStack = _ref2.normalizeStack,
      config = _ref2.config,
      field = _ref2.field;
  field !== null && field !== void 0 ? field : field = fieldToFormat(fieldOrDatumDef, expr, normalizeStack);

  if (field !== 'datum.value' && // For axis/legend, we can't correctly know the end of the bin from `datum`
  isFieldDef(fieldOrDatumDef) && isBinning(fieldOrDatumDef.bin)) {
    var endField = vgField(fieldOrDatumDef, {
      expr: expr,
      binSuffix: 'end'
    });
    return {
      signal: binFormatExpression(field, endField, format, formatType, config)
    };
  }

  return {
    signal: customFormatExpr(formatType, field, format)
  };
}
export function guideFormat(fieldOrDatumDef, type, format, formatType, config, omitTimeFormatConfig // axis doesn't use config.timeFormat
) {
  var _a;

  if (isCustomFormatType(formatType)) {
    return undefined; // handled in encode block
  } else if (format === undefined && formatType === undefined && config.numberFormatType && config.customFormatTypes) {
    return undefined; // handled in encode block
  }

  if (isFieldOrDatumDefForTimeFormat(fieldOrDatumDef)) {
    var timeUnit = isFieldDef(fieldOrDatumDef) ? (_a = normalizeTimeUnit(fieldOrDatumDef.timeUnit)) === null || _a === void 0 ? void 0 : _a.unit : undefined;
    return timeFormat(format, timeUnit, config, omitTimeFormatConfig);
  }

  return numberFormat(type, format, config);
}
export function guideFormatType(formatType, fieldOrDatumDef, scaleType) {
  var _a;

  if (formatType && (isSignalRef(formatType) || formatType === 'number' || formatType === 'time')) {
    return formatType;
  }

  if (isFieldOrDatumDefForTimeFormat(fieldOrDatumDef) && scaleType !== 'time' && scaleType !== 'utc') {
    return isFieldDef(fieldOrDatumDef) && ((_a = normalizeTimeUnit(fieldOrDatumDef === null || fieldOrDatumDef === void 0 ? void 0 : fieldOrDatumDef.timeUnit)) === null || _a === void 0 ? void 0 : _a.utc) ? 'utc' : 'time';
  }

  return undefined;
}
/**
 * Returns number format for a fieldDef.
 */

export function numberFormat(type, specifiedFormat, config) {
  // Specified format in axis/legend has higher precedence than fieldDef.format
  if (isString(specifiedFormat)) {
    return specifiedFormat;
  }

  if (type === QUANTITATIVE) {
    // we only apply the default if the field is quantitative
    return config.numberFormat;
  }

  return undefined;
}
/**
 * Returns time format for a fieldDef for use in guides.
 */

export function timeFormat(specifiedFormat, timeUnit, config, omitTimeFormatConfig) {
  if (specifiedFormat) {
    return specifiedFormat;
  }

  if (timeUnit) {
    return {
      signal: timeUnitSpecifierExpression(timeUnit)
    };
  }

  return omitTimeFormatConfig ? undefined : config.timeFormat;
}

function formatExpr(field, format) {
  return "format(".concat(field, ", \"").concat(format || '', "\")");
}

function binNumberFormatExpr(field, format, formatType, config) {
  var _a;

  if (isCustomFormatType(formatType)) {
    return customFormatExpr(formatType, field, format);
  }

  return formatExpr(field, (_a = isString(format) ? format : undefined) !== null && _a !== void 0 ? _a : config.numberFormat);
}

export function binFormatExpression(startField, endField, format, formatType, config) {
  if (format === undefined && formatType === undefined && config.customFormatTypes && config.numberFormatType) {
    return binFormatExpression(startField, endField, config.numberFormat, config.numberFormatType, config);
  }

  var start = binNumberFormatExpr(startField, format, formatType, config);
  var end = binNumberFormatExpr(endField, format, formatType, config);
  return "".concat(fieldValidPredicate(startField, false), " ? \"null\" : ").concat(start, " + \"").concat(BIN_RANGE_DELIMITER, "\" + ").concat(end);
}
/**
 * Returns the time expression used for axis/legend labels or text mark for a temporal field
 */

export function timeFormatExpression(field, timeUnit, format, rawTimeFormat, // should be provided only for actual text and headers, not axis/legend labels
isUTCScale) {
  if (!timeUnit || format) {
    // If there is no time unit, or if user explicitly specifies format for axis/legend/text.
    format = isString(format) ? format : rawTimeFormat; // only use provided timeFormat if there is no timeUnit.

    return "".concat(isUTCScale ? 'utc' : 'time', "Format(").concat(field, ", '").concat(format, "')");
  } else {
    return formatExpression(timeUnit, field, isUTCScale);
  }
}